import { CORE_API_URL } from "../../../../config/api";

// ITC classification report (#612) — reads the itc_classification store via
// cockpit-core (same path as bill validation), so it doesn't need a CI4 route.

export interface ItcReportLine {
  jobid: string;
  line_index: number;
  zoho_partyid: string;
  party_name: string | null;
  bill_date: string | null;
  line_description: string | null;
  hsn_or_sac: string | null;
  tax_amount: string | number | null;
  tax_specification: string | null;
  cgst_amount: string | number | null;
  sgst_amount: string | number | null;
  igst_amount: string | number | null;
  cess_amount: string | number | null;
  itc_status: string;
  section17_category: string | null;
  source_layer: string;
  reason: string | null;
  /**
   * ZOUR-93 — the GST registration whose input credit this line is.
   *
   * `location_key` is the folded form ('' = the primary, which is also where a
   * line whose bill has no location lands); the name and GSTIN are resolved
   * server-side so the table does not have to map Zoho ids.
   */
  location_key?: string;
  location_id?: string | null;
  location_name?: string | null;
  location_gstin?: string | null;
}

/** ZOUR-93 — one of the company's GST registrations, as the ITC filter needs it. */
export interface ItcRegistration {
  zoho_location_id: string | null;
  name: string;
  gstin: string | null;
  is_primary: boolean;
  is_active: boolean;
}

export interface ItcReport {
  counts: Record<string, number>;
  taxByStatus: Record<string, number>;
  blocked: { cgst: number; sgst: number; igst: number; cess: number; total: number };
  lines: ItcReportLine[];
  /**
   * ZOUR-93 — the registrations this report can be split by.
   *
   * `hasMultiple` false means the filter and the column stay hidden and the
   * page reads exactly as it did before this ticket.
   */
  registrations: ItcRegistration[];
  hasMultiple: boolean;
}

export async function getItcReport(
  from?: string,
  to?: string,
  /**
   * ZOUR-93 — one registration's Zoho location id. Omitted = every
   * registration, which is what this call always did.
   *
   * Input tax credit is claimed per GSTIN in that registration's own GSTR-3B,
   * so merged totals cannot be tied to a return.
   */
  location?: string,
): Promise<ItcReport> {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  if (location) qs.set("location", location);
  const res = await fetch(
    `${CORE_API_URL}/api_itc_report.php?${qs.toString()}`,
  );
  const json = await res.json();
  if (!json?.success) {
    throw new Error(json?.error || "Failed to load ITC report");
  }
  return {
    counts: json.summary?.counts ?? {},
    taxByStatus: json.summary?.tax_by_status ?? {},
    blocked: json.summary?.blocked ?? {
      cgst: 0,
      sgst: 0,
      igst: 0,
      cess: 0,
      total: 0,
    },
    lines: json.lines ?? [],
    registrations: json.locations?.list ?? [],
    hasMultiple: json.locations?.has_multiple === true,
  };
}
